import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import PageCrossLink from "@/components/PageCrossLink";
import { ARDUINO } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Save,
  FileCode2,
  Wand2,
  Hammer,
  Download,
  CheckCircle2,
  XCircle,
  Sparkles,
  Pencil,
  Usb,
} from "lucide-react";
import { toast } from "sonner";
import FlashWebSerialModal from "@/components/FlashWebSerialModal";

// Tiny token-based highlighter for Arduino/ESP32 keywords (visual only — textarea remains editable)
const KEYWORDS = new Set([
  "void", "int", "long", "float", "double", "bool", "char", "const", "static", "if", "else",
  "for", "while", "return", "true", "false", "include", "define", "setup", "loop", "delay",
  "Serial", "WiFi", "HTTPClient", "HTTPUpdate", "ArduinoJson", "StaticJsonDocument",
  "pinMode", "digitalWrite", "digitalRead", "analogRead", "attachInterrupt", "IRAM_ATTR",
  "map", "millis", "interrupts", "noInterrupts", "INPUT", "OUTPUT", "INPUT_PULLUP", "HIGH", "LOW",
  "RISING", "FALLING", "CHANGE",
]);

function HighlightedPreview({ code }) {
  // Render first 80 lines for performance
  const lines = code.split("\n").slice(0, 80);
  return (
    <pre className="bg-muted rounded-md p-3 text-xs font-mono overflow-x-auto max-h-[200px]">
      {lines.map((line, i) => (
        <div key={i}>
          {line.split(/(\s+|[(){};,])/).map((tok, j) => {
            if (KEYWORDS.has(tok)) return <span key={j} className="text-primary font-semibold">{tok}</span>;
            if (tok.match(/^".*"$/)) return <span key={j} className="text-accent">{tok}</span>;
            if (tok.match(/^\/\/.*/)) return <span key={j} className="text-muted-foreground italic">{tok}</span>;
            if (tok.match(/^[0-9]+$/)) return <span key={j} className="text-emerald-600 dark:text-emerald-400">{tok}</span>;
            return <span key={j}>{tok}</span>;
          })}
        </div>
      ))}
      {code.split("\n").length > 80 && <div className="text-muted-foreground mt-2">… ({code.split("\n").length - 80} baris lainnya disembunyikan di preview)</div>}
    </pre>
  );
}

export default function Arduino() {
  const { currentId, current } = useProject();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "project_admin";

  const [doc, setDoc] = useState(null);
  const [code, setCode] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [compiling, setCompiling] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [lastCompile, setLastCompile] = useState(null); // { version, download_url, size, md5 }
  const [flashOpen, setFlashOpen] = useState(false);

  const fetch_ = async () => {
    if (!currentId) return;
    const { data } = await api.get(`/projects/${currentId}/code`);
    setDoc(data);
    setCode(data.code_content || "");
    setDirty(false);
  };

  useEffect(() => { fetch_(); /* eslint-disable-next-line */ }, [currentId]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/projects/${currentId}/code`, { code_content: code });
      setDoc(data);
      setDirty(false);
      toast.success("Sketch berhasil diperbarui");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const regenerateFromWizard = async () => {
    if (dirty && !window.confirm("Ada perubahan yang belum disimpan. Wizard akan menimpa sketch. Lanjutkan?")) return;
    setRegenerating(true);
    try {
      await api.get(`/projects/${currentId}/esp32-sketch`);
      await fetch_();
      toast.success("Sketch di-generate ulang dari Wizard");
    } catch (e) {
      toast.error("Gagal regenerate");
    } finally {
      setRegenerating(false);
    }
  };

  const compile = async () => {
    if (dirty) {
      if (!window.confirm("Sketch belum disimpan. Simpan dulu sebelum compile?")) return;
      await save();
    }
    setCompiling(true);
    try {
      const { data } = await api.post(`/projects/${currentId}/code/compile`);
      if (data.ok) {
        toast.success(`Sketch berhasil di-compile (${data.version}) · ${(data.size/1024).toFixed(1)} KB`);
        setLastCompile(data);
        await fetch_();
      } else {
        setLastCompile(data);
        toast.error("Compile gagal — lihat log");
        await fetch_();
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 503) {
        toast.error(detail || "arduino-cli belum terpasang di server");
      } else {
        toast.error(detail || "Compile error");
      }
      await fetch_();
    } finally {
      setCompiling(false);
    }
  };

  const downloadBin = async () => {
    if (!lastCompile?.download_url) {
      toast.info("Belum ada hasil compile pada sesi ini. Klik Compile terlebih dahulu.");
      return;
    }
    try {
      const resp = await api.get(lastCompile.download_url.replace(/^\/api/, ""), { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cinnabit_${lastCompile.version}.bin`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Downloaded cinnabit_${lastCompile.version}.bin`);
    } catch (e) {
      toast.error("Gagal mengunduh .bin");
    }
  };

  if (!currentId) return <div className="text-muted-foreground">Pilih proyek terlebih dulu.</div>;

  const sourceBadge = doc?.source === "manual"
    ? <Badge className="bg-accent/10 text-accent border-accent/20"><Pencil className="w-3 h-3 mr-1" /> Edited manually</Badge>
    : <Badge className="bg-primary/10 text-primary border-primary/20"><Sparkles className="w-3 h-3 mr-1" /> Generated by Wizard</Badge>;

  const compileBadge = doc?.last_compile_status === "success"
    ? <Badge className="bg-primary/10 text-primary border-primary/20"><CheckCircle2 className="w-3 h-3 mr-1" /> Compile success</Badge>
    : doc?.last_compile_status === "failed"
    ? <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" /> Compile failed</Badge>
    : null;

  return (
    <div>
      <PageCrossLink
        from="Kode Arduino"
        to={{ label: "ESP32 Wizard", href: "/wizard", description: "Device key, sketch generator, kalibrasi, live monitor" }}
      />
      <PageHeader
        title="Kode Arduino"
        overline={current?.name}
        description="Editor sketch firmware ESP32. Tersinkron otomatis dengan ESP32 Wizard. Compile & download .bin langsung dari sini."
        actions={
          <div className="flex gap-2 flex-wrap">
            {isAdmin && (
              <>
                <Button variant="outline" onClick={regenerateFromWizard} disabled={regenerating} data-testid="arduino-regenerate">
                  <Wand2 className="w-4 h-4 mr-1" /> {regenerating ? "Generating…" : "Generate dari Wizard"}
                </Button>
                <Button variant="outline" onClick={compile} disabled={compiling} data-testid="arduino-compile">
                  <Hammer className="w-4 h-4 mr-1" /> {compiling ? "Compiling…" : "Compile"}
                </Button>
                <Button onClick={save} disabled={saving || !dirty} data-testid={ARDUINO.saveButton}>
                  <Save className="w-4 h-4 mr-1" /> {saving ? "Menyimpan…" : "Simpan"}
                </Button>
              </>
            )}
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {sourceBadge}
        {compileBadge}
        {lastCompile?.ok && (
          <>
            <Button variant="outline" size="sm" onClick={downloadBin} data-testid="arduino-download-bin">
              <Download className="w-4 h-4 mr-1" /> Download .bin ({lastCompile.version})
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => setFlashOpen(true)}
              data-testid="arduino-flash-usb"
            >
              <Usb className="w-4 h-4 mr-1" /> Flash via USB
            </Button>
          </>
        )}
        {doc?.binary_filename && !lastCompile && (
          <Badge variant="outline" className="border-primary/40 text-primary">
            <Download className="w-3 h-3 mr-1" /> Compiled binary tersedia di OTA Firmware
          </Badge>
        )}
        {doc?.updated_at && (
          <span className="text-xs text-muted-foreground ml-auto">
            Diubah {new Date(doc.updated_at).toLocaleString()}
          </span>
        )}
        {dirty && <Badge variant="outline" className="border-accent text-accent">UNSAVED</Badge>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-border rounded-lg bg-card p-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
            <FileCode2 className="w-4 h-4" />
            <span className="font-mono">cinnabit_{currentId.slice(0, 8)}.ino</span>
          </div>
          <Textarea
            value={code}
            onChange={(e) => { setCode(e.target.value); setDirty(true); }}
            className="font-mono text-xs min-h-[480px] resize-y"
            spellCheck={false}
            readOnly={!isAdmin}
            data-testid={ARDUINO.editor}
          />
          {!isAdmin && (
            <p className="mt-2 text-xs text-muted-foreground">Hanya Admin/Admin Anggota proyek ini yang dapat mengedit sketch.</p>
          )}
        </div>

        <div className="lg:col-span-1 space-y-4">
          <div className="border border-border rounded-lg bg-card p-4">
            <div className="overline text-muted-foreground mb-2">Syntax preview</div>
            <HighlightedPreview code={code} />
            <p className="mt-2 text-xs text-muted-foreground">Highlighting hanya visual — edit di kolom editor utama.</p>
          </div>

          {doc?.last_compile_log && (
            <div className="border border-border rounded-lg bg-card p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="overline text-muted-foreground">Compile log</div>
                {doc.last_compile_status === "success" ? (
                  <span className="text-xs text-primary">success</span>
                ) : (
                  <span className="text-xs text-destructive">failed</span>
                )}
              </div>
              <pre className="bg-muted rounded-md p-3 text-[10px] font-mono overflow-x-auto max-h-[300px] whitespace-pre-wrap">{doc.last_compile_log}</pre>
            </div>
          )}

          <Alert>
            <Hammer className="w-4 h-4" />
            <AlertDescription className="text-xs">
              Tombol <strong>Compile</strong> memerlukan <code className="font-mono">arduino-cli</code> terpasang di server.
              Hasil .bin otomatis terdaftar di menu <strong>OTA Firmware</strong> dengan version <code className="font-mono">compiled-&lt;timestamp&gt;</code>.
              Klik <strong>Aktifkan</strong> di sana untuk push OTA ke ESP32.
            </AlertDescription>
          </Alert>
        </div>
      </div>

      <FlashWebSerialModal
        projectId={currentId}
        version={lastCompile?.version}
        open={flashOpen}
        onClose={() => setFlashOpen(false)}
      />
    </div>
  );
}
